import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { AnimatePresence, motion } from 'framer-motion';
import styles from './RenderModal.module.css';

// Redux
import { closeModal } from '../../redux/slices/modalSlice';

// Components
import MainModal from '../MainModal/MainModal';
import SimpleFormModal from '../SimpleFormModal/SimpleFormModal';

// Modals
import AddStudentModal from '../AddStudentModal/AddStudentModal';
import AddStaffModal from '../AddStaffModal/AddStaffModal';
import AddRoomDetails from '../AddRoomDetails/AddRoomDetails';
import AddTemplate from '../AddTemplate/AddTemplate';
import UpdateRoom from '../UpdateRoom/UpdateRoom';
import TransferStudentModal from '../TransferStudentModal/TransferStudentModal';
import StatusUpdate from '../StatusUpdate/StatusUpdate';
import CreateFolderCard from '../CreateFolderCard/CreateFolderCard';
import GroupUpdate from '../GroupUpdate/GroupUpdate';
import Delete from '../Delete/Delete';
import BulkUpload from '../BulkUpload/BulkUpload';
import AddStudentsToGroup from '../AddStudentsToGroup/AddStudentsToGroup';
import AddLabel from '../AddLabel/AddLabel';
import StudentCheckIn from '../StudentCheckIn/StudentCheckIn';
import StudentCheckOut from '../StudentCheckOut/StudentCheckOut';
import AbsentDetails from '../AbsentDetails/AbsentDetails';
import EditDetails from '../EditDetails/EditDetails';
import StaffCheckIn from '../StaffCheckIn/StaffCheckIn';
import StaffCheckOut from '../StaffCheckOut/StaffCheckOut';
import StudentNewLeave from '../StudentNewLeave/StudentNewLeave';
import ViewLeave from '../ViewLeave/ViewLeave';
import CheckAvailability from '../CheckAvailability/CheckAvailability';
import AddProgram from '../AddProgram/AddProgram';
import ProgramLevelMovement from '../ProgramLevelMovement/ProgramLevelMovement';
import StudentMovement from '../StudentMovement/StudentMovement';
import ChooseRange from '../ChooseRange/ChooseRange';
import AddEnquiry from '../AddEnquiry/AddEnquiry';
import CreateAutomationRule from '../CreateAutomationRule/CreateAutomationRule';
import CreateForm from '../CreateForm/CreateForm';
import ConfigureColumn from '../ConfigureColumn/ConfigureColumn';
import FeeComponent from '../FeeComponent/FeeComponent';
import AddDiscount from '../AddDiscount/AddDiscount';
import AddTaxrate from '../AddTaxrate/AddTaxrate';
import AddRegister from '../AddRegister/AddRegister';
import CreateFeePlan from '../CreateFeePlan/CreateFeePlan';
import CreateInvoice from '../CreateInvoice/CreateInvoice';
import StaffAnnouncement from '../StaffAnnouncement/StaffAnnouncement';
import NewProcess from '../NewProcess/NewProcess';

import DynamicReportModal from '../DynamicReportModal/DynamicReportModal';
import MailInviteModal from '../MailInviteModal/MailInviteModal';

import AddSchedule from '../AddSchedule/AddSchedule';
import AddScheduleItem from '../AddScheduleItem/AddScheduleItem';
import CreateFeeComponent from '../CreateFeeComponent/CreateFeeComponent';
import HolidayCalendar from '../HolidayCalendar/HolidayCalendar';
import AddEvent from '../AddEvent/AddEvent';
import SelectedStudents from '../SelectedStudents/SelectedStudents';
import CreateNewsletter from '../CreateNewsLetter/CreateNewsLetter';
import Newsletter from '../Newsletter/Newsletter';
import CreateActivity from '../CreateActivity/CreateActivity';
import PostActivity from '../PostActivity/PostActivity';
import AddDeposit from '../AddDeposit/AddDeposit';
import AddCredit from '../AddCredit/AddCredit';

import CreateDebitNotes from '../CreateDebitNotes/CreateDebitNotes';
import AccountingStudentBalanceCreateInvoice from '../AccountingStudentBalanceCreateInvoice/AccountingStudentBalanceCreateInvoice';
import AccountingInvoice from '../AccountingInvoice/AccountingInvoice';

// Icons
import { FaUsers } from 'react-icons/fa';
import { MdGroups } from 'react-icons/md';
import { IoIosListBox } from 'react-icons/io';
import { RiFileTextLine } from 'react-icons/ri';
import UploadDocument from '../UploadDocument/UploadDocument';
import AddObservation from '../AddObservation/AddObservation';
import SelectStudents from '../SelectStudents/SelectStudents';
import AttendanceRoom from '../AttendanceRoom/AttendanceRoom';
import AddCreditBalance from '../Profile/AddCreditBalance/AddCreditBalance';
import RefundCreditAmount from '../Profile/RefundCreditAmount/RefundCreditAmount';
import AddFolder from '../AddFolder/AddFolder';
import AddDocument from '../AddDocument/AddDocument';
import AddNote from '../AddNote/AddNote';
import EditNote from '../EditNote/EditNote';
import SendRemainder from '../SendRemainder/SendRemainder';
import AddVaccinationNote from '../AddVaccinationNote/AddVaccinationNote';
import LogsModal from '../LogsModal/LogsModal';
import LeaveDetails from '../LeaveDetails/LeaveDetails';
import AddNewForm from '../AddNewForm/AddNewForm';
import QRCodeList from '../QRCodeList/QRCodeList';
import QRCode from '../QRCode/QRCode';
import AddDepositBalance from '../Profile/AddDepositBalance/AddDepositBalance';
import RefundDepositAmount from '../Profile/RefundDepositAmount/RefundDepositAmount';
import TransferToCredit from '../Profile/TransferToCredit/TransferToCredit';

// Report Config
const reportConfigs = {
  excelDynamicReport: {
    title: 'Weekly Attendance Report',
    lastGenerated: '22/06/2025 8:11 pm by Darshan',
    showDateRange: true,
    showFilter: true,
    showRoomSelect: true,
    showStatusSelect: false,
    showAutoFill: true,
  },
};

function RenderModal() {
  const activeModal = useSelector((state) => state.modal.type);
  const dispatch = useDispatch();
  const [formValue, setFormValue] = useState('');
  const modalData = useSelector((state) => state.modal.modalData);

  const allModals = {
    //AttendaceSectionDashboard
    attendanceRoom: (
      <AttendanceRoom
        data={modalData}
        isOpen={true}
        onClose={() => dispatch(closeModal())}
      />
    ),

    addStudent: <AddStudentModal />,
    addStaff: <AddStaffModal />,
    addroom: <AddRoomDetails />,
    addTemplete: <AddTemplate />,
    updateRoom: <UpdateRoom />,
    TransferStudent: <TransferStudentModal />,
    statusUpdate: <StatusUpdate />,
    createFolder: <CreateFolderCard />,
    updateGroups: <GroupUpdate />,
    delete: <Delete />,
    bulkUploadStaff: (
      <BulkUpload
        heading="Add Multiple Staff"
        icon={<MdGroups size={20} color="blue" />}
      />
    ),
    bulkUploadStudent: (
      <BulkUpload
        heading="Add Multiple Students"
        icon={<MdGroups size={20} color="blue" />}
      />
    ),
    addGroup: (
      <SimpleFormModal
        icon={<FaUsers />}
        heading="Add Group"
        fieldLabel="Group Name"
        fieldName="group"
        fieldPlaceholder="Enter group name"
        fieldValue={formValue}
        onChange={(e) => setFormValue(e.target.value)}
        onSave={() => {
          console.log('Saved:', formValue);
          dispatch(closeModal());
        }}
        onCancel={() => setFormValue('')}
      />
    ),
    AddStudentToGroup: <AddStudentsToGroup />,
    addLabel: <AddLabel />,
    addTemplate: <AddTemplate />,

    checkInStudent: <StudentCheckIn />,
    checkOutStudent: <StudentCheckOut />,
    absentDetails: <AbsentDetails />,
    editDetails: <EditDetails />,
    checkInStaff: <StaffCheckIn />,
    checkOutStaff: <StaffCheckOut />,
    newLeave: <StudentNewLeave />,
    viewLeave: <ViewLeave />,
    checkAvailability: <CheckAvailability />,
    addProgram: <AddProgram />,
    programLevelMovement: <ProgramLevelMovement />,
    studentMovement: <StudentMovement />,
    chooseRange: <ChooseRange />,
    addEnquiry: <AddEnquiry />,
    createAutomation: <CreateAutomationRule />,

    createForm: <CreateForm />,
    configureColumn: <ConfigureColumn />,
    feeComponent: <FeeComponent />,
    addDiscount: <AddDiscount />,
    addTaxrate: <AddTaxrate />,
    addRegister: <AddRegister />,
    addFeePlan: <CreateFeePlan />,
    createInvoice: <CreateInvoice />,
    staffAnnouncement: <StaffAnnouncement />,
    newProcess: <NewProcess />,

    //Accounting
    // accountingStudentBalanceCreateInvoice: (
    //   <AccountingStudentBalanceCreateInvoice />
    // ),
    addCredit: <AddCredit />,
    addDeposit: <AddDeposit />,

    accountingStudentBalanceCreateInvoice: (
      <AccountingStudentBalanceCreateInvoice />
    ),
    accountingInvoice: <AccountingInvoice />,

    createDebitNotes: <CreateDebitNotes />,

    addRegister: (
      <SimpleFormModal
        icon={<IoIosListBox />}
        heading="Add Register"
        fieldLabel="Register Name"
        fieldName="register"
        fieldPlaceholder="Set Register Name"
        fieldValue={formValue}
        onChange={(e) => setFormValue(e.target.value)}
        onSave={() => {
          console.log('Saved:', formValue);
          dispatch(closeModal());
        }}
        saveLabel="Set Range"
        onCancel={() => setFormValue('')}
      />
    ),

    //Inject config here mauagely
    excelDynamicReport: (
      <DynamicReportModal config={reportConfigs['excelDynamicReport']} />
    ),
    mailInviteParentSend: <MailInviteModal mode="send" role="parent" />,
    mailInviteParentEdit: <MailInviteModal mode="edit" role="parent" />,
    mailInviteStaffSend: <MailInviteModal mode="send" role="staff" />,
    mailInviteStaffEdit: <MailInviteModal mode="edit" role="staff" />,

    //assessments
    //curriculam
    editDocument: (
      <SimpleFormModal
        heading="Add Folder"
        fieldLabel="Name"
        fieldName="document"
        fieldPlaceholder="Enter folder name"
        fieldValue={formValue}
        onChange={(e) => setFormValue(e.target.value)}
        onSave={() => {
          console.log('Edited:', formValue);
          dispatch(closeModal());
        }}
        onCancel={() => setFormValue('')}
      />
    ),
    uploadDocument: <UploadDocument />,
    //Learning Journey
    editTitle: (
      <SimpleFormModal
        heading="Edit Title"
        fieldLabel="Title"
        fieldName="Title"
        fieldPlaceholder="Enter new title"
        fieldValue={formValue}
        onChange={(e) => setFormValue(e.target.value)}
        onSave={() => {
          console.log('Edited:', formValue);
          dispatch(closeModal());
        }}
        onCancel={() => setFormValue('')}
      />
    ),
    editNotes: (
      <SimpleFormModal
        heading="Edit Notes"
        fieldLabel="Notes"
        fieldName="notes"
        fieldPlaceholder="Notes..."
        fieldValue={formValue}
        onChange={(e) => setFormValue(e.target.value)}
        onSave={() => {
          console.log('Saved notes:', formValue);
          dispatch(closeModal());
        }}
        onCancel={() => setFormValue('')}
        customField={
          <div style={{ padding: '1rem 0' }}>
            <label className={styles.label}>Notes</label>
            <textarea
              placeholder="Notes..."
              value={formValue}
              onChange={(e) => setFormValue(e.target.value)}
              className={styles.textarea}
            />
          </div>
        }
      />
    ),

    addObservation: <AddObservation />,
    selectStudents: <SelectStudents />,
    //Calender

    addSchedule: <AddSchedule />,
    addScheduleItem: <AddScheduleItem />,
    createFeeComponent: <CreateFeeComponent />,
    holidayCalendar: <HolidayCalendar />,
    addEvent: <AddEvent />,

    createActivity: <CreateActivity />,
    postActivity: <PostActivity />,

    selectedStudents: <SelectedStudents data={modalData} />,
    createNewsLetter: <CreateNewsletter />,
    newsLetter: <Newsletter />,

    QRCode: <QRCode />,

    // Profile Modals
    // Profile Modals

    addDepositBalance: <AddDepositBalance />,
    refundDeposit: <RefundDepositAmount />,
    transferToCredit: <TransferToCredit />,
    addCreditBalance: <AddCreditBalance />,
    refundCreditAmount: <RefundCreditAmount />,

    addFolder: <AddFolder />,
    addDocument: <AddDocument />,
    addNote: <AddNote />,
    editNote: <EditNote />,
    sendRemainder: <SendRemainder />,
    addVaccinationNote: <AddVaccinationNote />,
    changeLogs: <LogsModal />,
    leaveDetails: <LeaveDetails />,
    addNewForm: <AddNewForm />,
    QRCodeList: <QRCodeList />,
  };

  return (
    <MainModal>
      <AnimatePresence mode="wait">
        {activeModal && (
          <motion.div
            key={activeModal}
            className={styles.RenderModal}
            initial={{ x: '100%', opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: '100%', opacity: 0 }}
            transition={{ type: 'tween', duration: 0.5, ease: 'easeInOut' }}
          >
            {allModals[activeModal]}
          </motion.div>
        )}
      </AnimatePresence>
    </MainModal>
  );
}

export default RenderModal;
